import React, { useState, useEffect } from 'react';
import { BusyIndicator, FlexBox, Button } from '@ui5/webcomponents-react';
import { IllustratedMessage } from '@ui5/webcomponents-react';
import '@ui5/webcomponents-fiori/dist/illustrations/SearchEarth.js';
import { fetchLocationData } from './api';
import AppShell from './AppShell';
import DeviceControlPage from './DeviceControlPage';
import GuestVerifyDialog from './GuestVerifyDialog';
import GuestChatbot from './GuestChatbot';
import './App.css';

import "@ui5/webcomponents-icons/dist/AllIcons.js";

// When embedded inside the native HomeAlerts Capacitor shell the guest app
// sits at `/guest/` — exiting the guest flow means going back to the
// OpenUI5 app's entry point one directory up.
const NATIVE_PARENT_URL = '../index.html';
const isNativeCapacitor = () =>
  typeof window !== 'undefined'
  && !!window.Capacitor
  && typeof window.Capacitor.isNativePlatform === 'function'
  && window.Capacitor.isNativePlatform();

function App() {
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState(false);
  const [locationData, setLocationData] = useState(null);
  const [page, setPage]             = useState('home'); // 'home' | 'controls'
  const [verified, setVerified]     = useState(false);
  const [guestCode, setGuestCode]   = useState('');
  const [showVerify, setShowVerify] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const isNative = isNativeCapacitor();

  useEffect(() => {
    setLoading(true);
    setError(false);
    setLocationData(null);

    const urlParams = new URLSearchParams(window.location.search);
    const uuid = urlParams.get('location');

    if (uuid) {
      fetchLocationData(uuid)
        .then(data => {
          if ((data.response && data.response.error) || data.error) {
            setError(true);
          } else {
            setLocationData(data);
          }
        })
        .catch(() => setError(true))
        .finally(() => setLoading(false));
    } else {
      setError(true);
      setLoading(false);
    }
  }, [refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Wire the Android hardware back button (and iOS swipe-back gesture via
  // Capacitor's App plugin). Without this, back-press on the home page would
  // exit the WebView entirely instead of returning to the OpenUI5 shell.
  useEffect(() => {
    if (!isNative) { return; }
    const appPlugin = window.Capacitor?.Plugins?.App;
    if (!appPlugin || typeof appPlugin.addListener !== 'function') { return; }

    let handle;
    const registration = appPlugin.addListener('backButton', () => {
      if (page === 'controls') {
        setPage('home');
      } else {
        window.location.href = NATIVE_PARENT_URL;
      }
    });

    // addListener may return the handle directly or a promise resolving to it
    Promise.resolve(registration).then(h => { handle = h; });

    return () => {
      if (handle && typeof handle.remove === 'function') { handle.remove(); }
    };
  }, [isNative, page]);

  const shellBarTitle = page === 'controls' ? 'Smart Home Controls' : 'Digital Guest Book';

  function navigateToControls() {
    if (verified) {
      setPage('controls');
    } else {
      setShowVerify(true);
    }
  }

  const mainCfg = locationData
    ? (Array.isArray(locationData.main) ? locationData.main[0] : locationData.main)
    : null;

  const showDeviceControl = !!mainCfg
    && parseInt(mainCfg.show_device_control) === 1
    && locationData.locationProfileDevices?.length > 0;

  const showAiChatbot = !!mainCfg && parseInt(mainCfg.show_ai_chatbot) === 1;
  const locationUUID  = new URLSearchParams(window.location.search).get('location');

  // Server's `navigate` tool returns a screen name; map to the two pages we have.
  function handleChatbotNavigate(route) {
    if (route === 'controls' || route === 'devices') {
      if (showDeviceControl) { navigateToControls(); }
    } else if (route === 'home' || route === 'guestbook') {
      setPage('home');
    }
  }

  return (
    <div
      className={`app-root app-root--${page}`}
      style={(page === 'home' || page === 'controls') ? {
        backgroundImage: `url(${process.env.PUBLIC_URL}/img/cleanBlue.jpg)`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed',
      } : undefined}
    >
      <div className="app-header">
        <div className="app-header-start">
          {page === 'controls' && (
            <Button icon="nav-back" design="Transparent" onClick={() => setPage('home')} />
          )}
          {page === 'home' && isNative && (
            <Button icon="nav-back" design="Transparent" onClick={() => { window.location.href = NATIVE_PARENT_URL; }} />
          )}
        </div>
        <div className="app-header-title">
          <span className="app-header-primary">{shellBarTitle}</span>
          <span className="app-header-secondary">by Home Alerts</span>
        </div>
        <div className="app-header-end">
          {showDeviceControl && page === 'home' && (
            <Button icon="home-share" design="Transparent" onClick={navigateToControls} />
          )}
          {isNative && page === 'controls' && (
            <Button icon="refresh" design="Transparent" onClick={() => setRefreshKey(k => k + 1)} disabled={loading} />
          )}
        </div>
      </div>
      <div className="app-content">
        {loading && (
          <FlexBox justifyContent="Center" alignItems="Center" style={{ minHeight: '60vh' }}>
            <BusyIndicator size="Large" active delay={0} />
          </FlexBox>
        )}
        {error && !loading && (
          <FlexBox direction="Column" justifyContent="Center" alignItems="Center" style={{ minHeight: '60vh', gap: '1rem' }}>
            <IllustratedMessage
              name="SearchEarth"
              titleText="Location Not Found"
              subtitleText="The welcome book for this location could not be loaded. Please check the QR code and try again."
            />
            <Button design="Emphasized" icon="refresh" onClick={() => setRefreshKey(k => k + 1)}>
              Retry
            </Button>
          </FlexBox>
        )}
        {!loading && !error && locationData && page === 'home' && (
          <AppShell
            locationData={locationData}
            showDeviceControl={showDeviceControl}
            onNavigateToControls={navigateToControls}
          />
        )}
        {!loading && !error && locationData && page === 'controls' && (
          <DeviceControlPage
            locationProfileDevices={locationData.locationProfileDevices || []}
            onBack={() => setPage('home')}
            locationUUID={new URLSearchParams(window.location.search).get('location')}
            guestCode={guestCode}
          />
        )}
      </div>

      <GuestVerifyDialog
        open={showVerify}
        uuid={new URLSearchParams(window.location.search).get('location')}
        onVerified={(code) => {
          setVerified(true);
          setGuestCode(code);
          setShowVerify(false);
          setPage('controls');
        }}
        onClose={() => setShowVerify(false)}
      />

      {!loading && !error && showAiChatbot && locationUUID && (
        <GuestChatbot uuid={locationUUID} onNavigate={handleChatbotNavigate} />
      )}
    </div>
  );
}

export default App;
